import React, { useEffect, useState, useCallback } from 'react';
import clsx from 'clsx';
import { useOutletContext } from 'react-router-dom';
import Icon from '../components/Icon';
import { fetchIntegrationsHealth, fetchCircleBalances } from '../services/api';
import { timeAgo } from '../utils/format';

const HEALTH_BADGE = {
  healthy:     { class: 'badge-success', label: 'Healthy'      },
  idle:        { class: 'badge-muted',   label: 'Idle (24h)'   },
  degraded:    { class: 'badge-warning', label: 'Degraded'     },
  failing:     { class: 'badge-danger',  label: 'Failing'      },
  unconfigured:{ class: 'badge-muted',   label: 'Not configured'},
};

const PROVIDER_ICON = {
  alchemy:    'dashboard',
  paytrie:    'wallet',
  yellowcard: 'wallet',
  sumsub:     'shield',
  circle:     'wallet',
  anthropic:  'bell',
};

const Integrations = () => {
  const { environment } = useOutletContext();

  const [providers, setProviders] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [circleProbe, setCircleProbe] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await fetchIntegrationsHealth(environment);
      setProviders(data.providers || []);
      setMeta({ generated_at: data.generated_at, supabase_ready: data.supabase_ready });
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load integrations health');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [environment]);

  useEffect(() => {
    let cancelled = false;
    load();
    const i = setInterval(() => { if (!cancelled) load(); }, 30000);
    return () => { cancelled = true; clearInterval(i); };
  }, [load]);

  const probeCircle = async () => {
    try {
      const data = await fetchCircleBalances(environment);
      setCircleProbe(data);
    } catch (err) {
      setCircleProbe({ ok: false, error: err.message });
    }
  };

  const counts = providers.reduce(
    (acc, p) => ({
      configured:   acc.configured   + (p.configured ? 1 : 0),
      healthy:      acc.healthy      + (p.health === 'healthy' ? 1 : 0),
      degraded:     acc.degraded     + (p.health === 'degraded' || p.health === 'failing' ? 1 : 0),
      total_24h:    acc.total_24h    + (p.events_24h || 0),
    }),
    { configured: 0, healthy: 0, degraded: 0, total_24h: 0 }
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Integrations</h1>
          <p>
            Live health of every external provider — KYC, on/off-ramps, on-chain feeds, custodial wallets, AI agent
            <span className="text-tx3"> · {environment}</span>
          </p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={load} disabled={refreshing} aria-label="Refresh integrations">
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="dashboard-grid stagger mb-4">
        <div className="card">
          <div className="card-title">Configured</div>
          <div className="stat-value" style={{ color: counts.configured === providers.length ? 'var(--ok)' : 'var(--warn)' }}>
            {counts.configured}<span className="text-tx3 text-md"> / {providers.length}</span>
          </div>
          <div className="stat-label">Providers wired</div>
        </div>
        <div className="card">
          <div className="card-title">Healthy</div>
          <div className="stat-value" style={{ color: 'var(--ok)' }}>{counts.healthy}</div>
          <div className="stat-label">No errors in last 24h</div>
        </div>
        <div className="card">
          <div className="card-title">Degraded / Failing</div>
          <div className="stat-value" style={{ color: counts.degraded > 0 ? 'var(--err)' : 'var(--tx3)' }}>
            {counts.degraded}
          </div>
          <div className="stat-label">Need attention</div>
        </div>
        <div className="card">
          <div className="card-title">Events (24h)</div>
          <div className="stat-value">{counts.total_24h.toLocaleString()}</div>
          <div className="stat-label">Across all providers</div>
        </div>
      </div>

      {error && (
        <div role="alert" style={{ background: 'var(--err-bg)', color: 'var(--err)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13, marginBottom: 'var(--s3)' }}>
          {error}
        </div>
      )}

      {loading && providers.length === 0 ? (
        <div className="dashboard-grid stagger">
          {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="skeleton skeleton-block" style={{ height: 180 }} />)}
        </div>
      ) : (
        <div className="dashboard-grid stagger">
          {providers.map((p) => (
            <ProviderCard
              key={p.id}
              provider={p}
              onProbe={p.id === 'circle' ? probeCircle : null}
              probeResult={p.id === 'circle' ? circleProbe : null}
            />
          ))}
        </div>
      )}

      {meta && (
        <div className="text-tx3 text-xs mt-4" style={{ textAlign: 'center' }}>
          Last refreshed {timeAgo(meta.generated_at)} · Supabase {meta.supabase_ready ? 'ready' : 'not ready'} · auto-refresh every 30s
        </div>
      )}
    </>
  );
};

const ProviderCard = ({ provider, onProbe, probeResult }) => {
  const badge = HEALTH_BADGE[provider.health] || HEALTH_BADGE.unconfigured;
  const accent = provider.health === 'healthy' ? 'var(--ok)'
    : provider.health === 'degraded' || provider.health === 'failing' ? 'var(--err)'
    : provider.health === 'idle' ? 'var(--info)'
    : 'var(--tx3)';

  const envVars = Object.entries(provider.env_vars || {});

  return (
    <div className="card" style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="row mb-3" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div className="row row-gap-3" style={{ alignItems: 'center' }}>
          <div style={{
            width: 40, height: 40,
            borderRadius: 'var(--r-md)',
            background: 'var(--bg-elevated)',
            color: accent,
            display: 'grid', placeItems: 'center',
          }}>
            <Icon name={PROVIDER_ICON[provider.id] || 'shield'} size={20} />
          </div>
          <div className="col col-gap-2">
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tx1)' }}>{provider.name}</div>
            <div className="text-tx3 text-xs">{provider.role}</div>
          </div>
        </div>
        <span className={clsx('badge', badge.class)}>{badge.label}</span>
      </div>

      <div className="row row-gap-4 mb-3" style={{ alignItems: 'flex-end' }}>
        <div className="col col-gap-2 flex-1">
          <div className="text-tx3 text-xs">Events (24h)</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--tx1)' }}>{provider.events_24h ?? 0}</div>
        </div>
        <div className="col col-gap-2 flex-1">
          <div className="text-tx3 text-xs">Errors (24h)</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: provider.errors_24h > 0 ? 'var(--err)' : 'var(--ok)' }}>
            {provider.errors_24h ?? 0}
          </div>
        </div>
        <div className="col col-gap-2 flex-1">
          <div className="text-tx3 text-xs">Last event</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx1)' }}>{timeAgo(provider.last_event_at)}</div>
        </div>
      </div>

      <div className="card-divider" />

      <div className="text-tx3 text-xs mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
        Env vars
      </div>
      <div className="col col-gap-2 mb-3">
        {envVars.length === 0 ? (
          <div className="text-tx3 text-xs">no env vars defined</div>
        ) : envVars.map(([name, set]) => (
          <div key={name} className="row" style={{ justifyContent: 'space-between', fontSize: 12 }}>
            <code style={{ color: 'var(--tx2)', fontFamily: 'var(--mono)' }}>{name}</code>
            <span className={clsx('badge', set ? 'badge-success' : 'badge-muted')}>{set ? 'set' : 'unset'}</span>
          </div>
        ))}
      </div>

      {provider.webhook_url && (
        <>
          <div className="text-tx3 text-xs mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
            Webhook URL
          </div>
          <code style={{
            display: 'block', fontSize: 11, color: 'var(--tx1)', fontFamily: 'var(--mono)',
            background: 'var(--bg-elevated)', padding: 'var(--s2) var(--s3)',
            borderRadius: 'var(--r-sm)', wordBreak: 'break-all', marginBottom: 'var(--s3)',
          }}>
            {provider.webhook_url}
          </code>
        </>
      )}

      {onProbe && (
        <button type="button" className="btn btn-outline" style={{ width: '100%', justifyContent: 'center', padding: '8px' }} onClick={onProbe}>
          Probe live
        </button>
      )}
      {probeResult && (
        <div style={{
          marginTop: 'var(--s3)', padding: 'var(--s3)', fontSize: 12,
          background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)',
          color: probeResult.ok ? 'var(--ok)' : 'var(--err)',
        }}>
          {probeResult.ok
            ? `OK · ${probeResult.wallets?.length ?? 0} wallets · ${probeResult.latency_ms}ms`
            : (probeResult.message || probeResult.error || 'probe failed')}
        </div>
      )}
    </div>
  );
};

export default Integrations;
